import type { OperationGroup } from '@features/playground.graphql'
import type { SdkOperationGroupInfoFragment } from '@features/sdk-explorer.graphql'

/**
 * Transforms the SdkOperationGroup[] into the OperationGroup[] structure,
 * so that Navigation component can display data.
 * @param operationGroups - Input array of groups and operations.
 * @returns An array of transformed products with their associated projects and operations.
 */
export function transformGroupsToNavigationArray(
  operationGroups: SdkOperationGroupInfoFragment[],
  language: string,
  version?: string,
): OperationGroup[] {
  // Group all operations by product key
  const groupedByProduct = operationGroups.reduce((acc, group) => {
    group.operations.forEach((operation) => {
      const productKey = operation.product.key
      if (!acc.has(productKey)) {
        acc.set(productKey, {
          key: productKey,
          name: operation.product.name,
          projects: [
            {
              key: 'common',
              name: 'Methods Overview',
              tags: [],
              operations: [],
            },
          ],
        })
      }

      const product = acc.get(productKey)
      if (product) {
        // Add unique tags
        const tag = operation.tags?.[0] || group.name
        if (!product.projects[0].tags.includes(tag)) {
          product.projects[0].tags.push(tag)
        }
        // Create slug
        const slug =
          '/' +
          [productKey, language, version, operation.name]
            .filter(Boolean)
            .join('/')

        // Add operation
        product.projects[0].operations.push({
          name: operation.name,
          path: `/${operation.name}`,
          summary: operation.summary || '',
          slug,
          project: 'common',
          group: productKey,
          tags: operation.tags ?? [group.name],
          method: '',
        })
      }
    })
    return acc
  }, new Map<string, OperationGroup>())

  // Convert the grouped object back to an array
  return Array.from(groupedByProduct.values())
}
